import { useMemo, useCallback } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation } from '../../hooks';
import { queryCache } from '../../cache';
import { FacilityService } from './service';
import {
  Facility,
  CreateFacilityRequest,
  FacilitiesQueryParams,
  FacilityQueryResponse,
} from './types';
import { useMergeSuggestions, useMergePreview, useMerge } from '../merge/hooks';
import { FACILITIES_MERGE_CONFIG } from '../merge/config';

const CACHE_KEY = 'facilities';

export function useFacilities(params?: FacilitiesQueryParams) {
  const api = useApi();
  const service = useMemo(() => new FacilityService(api), [api]);

  // TODO: Enhance cache invalidation to be more targeted
  // Create a cache key that includes query parameters
  // const cacheKey = useMemo(() => {
  //   return `${CACHE_KEY}`;
  // }, [params]);

  const fetcher = useCallback(() => service.getAll(params), [service, params]);

  // TODO: Enhance cache invalidation to be more targeted
  return useQuery<FacilityQueryResponse>(CACHE_KEY, fetcher);
}

export function useFacility(id: number) {
  const api = useApi();
  const service = useMemo(() => new FacilityService(api), [api]);
  const fetcher = useCallback(() => service.getById(id), [service, id]);

  return useQuery<Facility>(`${CACHE_KEY}`, fetcher);
}

export function useFacilitiesByManufacturer(manufacturerId: number | null) {
  const api = useApi();
  const service = useMemo(() => new FacilityService(api), [api]);
  const fetcher = useCallback(() => {
    if (manufacturerId === null) {
      throw new Error('Manufacturer ID is required');
    }
    return service.getByManufacturer(manufacturerId);
  }, [service, manufacturerId]);

  return useQuery<{ facilities: Facility[] }>(
    `${CACHE_KEY}-manufacturer-${manufacturerId || 'null'}`,
    fetcher,
    {
      enabled: manufacturerId !== null,
    }
  );
}

export function useCreateFacility() {
  const api = useApi();
  const service = useMemo(() => new FacilityService(api), [api]);

  return useMutation<Facility, CreateFacilityRequest>(
    data => service.create(data),
    {
      onSuccess: _ => {
        // Invalidate the general facilities list
        queryCache.invalidate(CACHE_KEY);
      },
    }
  );
}

export function useUpdateFacility() {
  const api = useApi();
  const service = useMemo(() => new FacilityService(api), [api]);

  return useMutation<Facility, Facility>(data => service.update(data), {
    onSuccess: _ => {
      queryCache.invalidate(CACHE_KEY);
    },
  });
}

export function useDeleteFacility() {
  const api = useApi();
  const service = useMemo(() => new FacilityService(api), [api]);

  return useMutation<void, number>(facilityId => service.delete(facilityId), {
    onSuccess: _ => {
      // Since we don't have the manufacturer_id after deletion,
      // we need to invalidate all facilities-related queries
      queryCache.invalidatePrefix(CACHE_KEY);
    },
  });
}

// Convenience hook that combines all facilities operations
export function useFacilitiesPage(params?: FacilitiesQueryParams) {
  const facilityResponse = useFacilities(params);
  const createFacility = useCreateFacility();
  const updateFacility = useUpdateFacility();
  const deleteFacility = useDeleteFacility();

  return useMemo(
    () => ({
      // Query operations
      facilities: facilityResponse,

      // Mutation operations
      create: createFacility,
      update: updateFacility,
      delete: deleteFacility,

      // Convenience getters for common states
      data:
        facilityResponse.status === 'success'
          ? (facilityResponse.data?.facilities ?? [])
          : [],
      total:
        facilityResponse.status === 'success'
          ? (facilityResponse.data?.total ?? 0)
          : 0,
      isLoading: facilityResponse.status === 'loading',
      isError: facilityResponse.status === 'error',
      error:
        facilityResponse.status === 'error' ? facilityResponse.error : null,

      // Actions
      refetch: facilityResponse.refetch,
      invalidate: facilityResponse.invalidate,
    }),
    [facilityResponse, createFacility, updateFacility, deleteFacility]
  );
}

// Merge hooks for facilities
export function useMergeFacilitiesSuggestions(facilityId: number | null) {
  return useMergeSuggestions<Facility>(FACILITIES_MERGE_CONFIG, facilityId);
}

export function useMergeFacilitiesPreview(
  sourceId: number | null,
  targetId: number | null
) {
  return useMergePreview(FACILITIES_MERGE_CONFIG, sourceId, targetId);
}

export function useMergeFacilities() {
  return useMerge(FACILITIES_MERGE_CONFIG, CACHE_KEY);
}
